
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type GroupListItem = {
  id: string;
  name: string;
  description: string | null;
  avatar_url: string | null;
  created_at: string;
  created_by: string;
  location: string | null;
  min_skill_level: number;
  max_skill_level: number;
  is_public: boolean;
  memberCount: number;
  isMember: boolean;
  isAdmin: boolean;
  invitationStatus: string | null;
  adminName: string;
  privacy: string;
  joinRequestStatus: string | null;
};

export const useUserGroupsOptimized = (userId?: string | null) => {
  const query = useQuery({
    queryKey: ['user-groups-optimized', userId],
    enabled: !!userId,
    staleTime: 60 * 1000, // 1 min SWR window
    gcTime: 5 * 60 * 1000, // 5 mins
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!userId) return [] as GroupListItem[];

      console.log('🚀 Calling RPC get_user_groups_optimized for:', userId);
      const { data, error } = await supabase.rpc('get_user_groups_optimized', { p_user_id: userId });

      if (error) {
        console.error('❌ RPC get_user_groups_optimized failed:', error);
        throw error;
      }

      const mapped: GroupListItem[] = (data || []).map((row: any) => ({
        id: row.id,
        name: row.name,
        description: row.description,
        avatar_url: row.avatar_url,
        created_at: row.created_at,
        created_by: row.created_by,
        location: row.location,
        min_skill_level: Number(row.min_skill_level),
        max_skill_level: Number(row.max_skill_level),
        is_public: !!row.is_public,
        memberCount: Number(row.member_count ?? 0),
        isMember: !!row.is_member,
        isAdmin: !!row.is_admin,
        invitationStatus: row.invitation_status ?? null,
        adminName: row.admin_name ?? 'Unknown Admin',
        privacy: row.privacy ?? (row.is_public ? 'public' : 'private'),
        joinRequestStatus: row.join_request_status ?? null,
      }));

      console.log('✅ Optimized groups loaded:', mapped.length);
      return mapped;
    },
    meta: {
      onError: (err: unknown) => {
        console.error('🔥 Error in useUserGroupsOptimized:', err);
      }
    }
  });

  return {
    groups: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
